import classNames from 'classnames';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {WebUI} from "../../wui-core/src/index"
import Icon from '../../wui-icon/src';
import {getLangInfo} from "../../wui-locale/src/tool";
import ProgressBar from '../../wui-progress/src';
import i18n from './i18n.js';
import { UploadListProps, DefaultListArr } from './iUpload'

const previewFile = (file: any, callback: (v: any)=>void) => {
    const reader = new FileReader();
    reader.onloadend = () => callback(reader.result);
    reader.readAsDataURL(file);
};

const defaultProps = {
    listType: 'text',
    progressAttr: {
        strokeWidth: 3,
        showInfo: false,
    },
    clsPrefix: 'u-upload',
    showRemoveIcon: false,
    showDownloadIcon: false,
    showPreviewIcon: true,
};

// 优化：缓存文件渲染项
const FileItem = React.memo<{
    file: DefaultListArr;
    index: number;
    listType: string;
    clsPrefix: string;
    showRemoveIcon: boolean;
    showDownloadIcon: boolean;
    showPreviewIcon: boolean;
    progress: any;
    locale: string;
    fieldid?: string;
    showUploadList: any;
    iconRender?: any;
    itemRender?: any;
    dragable?: boolean;
    disabled?: boolean;
    removeText?: string;
    downloadText?: string;
    onPreview:(file: DefaultListArr, e: React.MouseEvent<HTMLElement>) => void;
    onRemove: (file: DefaultListArr) => void;
    onDownload: (file: DefaultListArr) => void;
    draggedIndex: number | null;
    dragOverIndex: number | null;
    onDragStart: (e: React.DragEvent, index: number) => void;
    onDragEnter: (index: number) => void;
    onDragEnd: () => void;
    itemRef: (el: HTMLDivElement | null) => void;
    isImageUrl: (file: any) => boolean;
        }>(({
            file, index, listType, clsPrefix, showRemoveIcon, showDownloadIcon, showPreviewIcon,
            progress, locale, fieldid, showUploadList, iconRender, itemRender, dragable, disabled,
            removeText, downloadText, onPreview, onRemove, onDownload, draggedIndex,
            onDragStart, onDragEnter, onDragEnd, itemRef, isImageUrl
        }) => {
            const local = getLangInfo(locale, i18n, 'upload');

            // 优化：缓存图标渲染
            const icon = useMemo(() => {
                let iconElement = <Icon type="uf-link"/>;

                if (listType === 'picture' || listType === 'picture-card') {
                    if (file.status === 'uploading' || (!file.thumbUrl && !file.url)) {
                        if (listType === 'picture-card') {
                            iconElement = iconRender ? iconRender() : (file.thumbUrl || file.url) ?
                                <a className={`${clsPrefix}-list-item-thumbnail`} target="_blank">
                                    {file?.thumbUrl && file?.url ? <img src={file.thumbUrl || file.url} alt={file.name}/> : null}
                                </a> : isImageUrl(file) ?
                                    <Icon type="uf-picture" /> :
                                    <div className={`${clsPrefix}-list-item-uploading-text`}>{local.langMap.fileLoading}</div>;
                        } else {
                            iconElement = iconRender ? iconRender() : (file.thumbUrl || file.url) ?
                                <a className={`${clsPrefix}-list-item-thumbnail`} target="_blank">
                                    <img src={file.thumbUrl || file.url} alt={file.name}/>
                                </a> : isImageUrl(file) ?
                                    <Icon type="uf-picture" /> :
                                    <Icon className={`${clsPrefix}-list-item-thumbnail`} type="uf-file-o-2"/>;
                        }
                    } else {
                        iconElement = (
                            <a
                                className={`${clsPrefix}-list-item-thumbnail`}
                                onClick={e => onPreview(file, e)}
                                href={file.url || file.thumbUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {iconRender ? iconRender() : file.thumbUrl || file.url ?
                                    <img src={file.thumbUrl || file.url} alt={file.name}/> :
                                    <Icon type="uf-picture" />
                                }
                            </a>
                        );
                    }
                } else {
                    iconElement = iconRender ? iconRender() : iconElement;
                }

                return iconElement;
            }, [file, listType, clsPrefix, iconRender, isImageUrl, local.langMap.fileLoading, onPreview]);

            // 优化：缓存进度条渲染
            const progressNode = useMemo(() => {
                if (file.status === 'uploading') {
                    if (progress === null) {
                        return null;
                    } else {
                        return (
                            <ProgressBar
                                size="sm"
                                percent={file.percent == 100 ? 99.99 : file.percent}
                                strokeWidth={progress?.strokeWidth}
                                strokeColor={progress?.strokeColor}
                                format={progress?.format}
                            />
                        );
                    }
                } else if (file.status === 'done') {
                    return null; // 移除完成状态的进度条显示
                }
                return null;
            }, [file.status, file.percent, progress]);

            // 优化：缓存预览元素
            const preview = useMemo(() => {
                return file.url ? (
                    <a
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${clsPrefix}-list-item-name`}
                        onClick={e => onPreview(file, e)}
                        title={file.name}
                    >
                        {file.name}
                    </a>
                ) : (
                    <span
                        className={`${clsPrefix}-list-item-name`}
                        onClick={e => onPreview(file, e)}
                        title={file.name}
                    >
                        {file.name}
                    </span>
                );
            }, [file.url, file.name, clsPrefix, onPreview]);

            // 优化：缓存操作按钮
            const actions = useMemo(() => {
                const style: React.CSSProperties | undefined = (file.url || file.thumbUrl) ? undefined : {
                    pointerEvents: 'none',
                    opacity: 0.5,
                };

                const previewIcon = showPreviewIcon ? (
                    <span
                        className={`${clsPrefix}-eye-o`}
                        style={style}
                        onClick={e => onPreview(file, e)}
                    >
                        <Icon fieldid={fieldid ? fieldid + "-preview" : undefined} title={local.langMap.toview} type="uf-eye-o"/>
                    </span>
                ) : null;

                let newRemoveIcon = null;
                if (showUploadList && typeof showUploadList !== "boolean" && showUploadList.removeIcon && typeof showUploadList.removeIcon !== "string") {
                    newRemoveIcon = React.cloneElement(showUploadList.removeIcon, {
                        title: removeText || local.langMap.remove,
                        onClick: () => onRemove(file),
                        className: `${clsPrefix}-customize-del-icon`
                    });
                }

                const removeIcon = showRemoveIcon ? (
                    newRemoveIcon ? newRemoveIcon :
                        <Icon type="uf-del" title={removeText || local.langMap.remove} fieldid={fieldid ? fieldid + "-del" : undefined}
                            onClick={() => onRemove(file)}/>
                ) : null;

                const downloadIcon = showDownloadIcon ? (
                    <Icon type="uf-download" title={downloadText || local.langMap.down} fieldid={fieldid ? fieldid + "-download" : undefined}
                        onClick={() => onDownload(file)}/>
                ) : null;

                const removeIconClose = !showRemoveIcon ? (disabled ? null :
                    <Icon type="uf-close" title={removeText || local.langMap.remove} fieldid={fieldid ? fieldid + "-close" : undefined}
                        onClick={() => onRemove(file)}/>
                ) : null;

                return (listType === 'picture-card' && file.status !== 'uploading') ? (
                    <span className={`${clsPrefix}-list-item-actions ${clsPrefix}-list-item-mask`}>
                        {previewIcon}
                        {removeIcon}
                    </span>
                ) : (
                    <span className={`${clsPrefix}-list-item-actions`}>
                        {removeIcon}
                        {file.status === 'done' && downloadIcon}
                        {removeIconClose}
                    </span>
                );
            }, [file, showPreviewIcon, showRemoveIcon, showDownloadIcon, showUploadList, listType, clsPrefix,
                fieldid, removeText, downloadText, disabled, local.langMap, onPreview, onRemove, onDownload]);

            const infoUploadingClass = classNames({
                [`${clsPrefix}-list-item`]: true,
                [`${clsPrefix}-list-item-${file.status}`]: true,
            });

            let iconNum = 'one';
            if (showDownloadIcon) {
                iconNum = 'two';
            }

            let cls = classNames({
                [`${clsPrefix}-list-item-info`]: true,
                [`${clsPrefix}-list-item-icon-num-${iconNum}`]: iconNum === 'two',
            });

            let item = dragable ? (
                <div
                    className={infoUploadingClass}
                    key={file.uid}
                    ref={itemRef}
                    draggable={!disabled}
                    onDragStart={(e) => onDragStart(e, index)}
                    onDragEnter={() => onDragEnter(index)}
                    onDragOver={(e) => e.preventDefault()}
                    onDragEnd={onDragEnd}
                    style={{
                        opacity: draggedIndex === index ? 0.5 : 1,
                        cursor: disabled ? 'default' : 'move'
                    }}
                >
                    <div className={cls}>
                        {icon}
                        {preview}
                        {actions}
                    </div>
                    {progressNode}
                </div>
            ) : (
                <div className={infoUploadingClass} key={file.uid}>
                    <div className={cls}>
                        {icon}
                        {preview}
                        {actions}
                    </div>
                    {progressNode}
                </div>
            );

            let listItemNode = itemRender ? itemRender(item, file, [], {
                download: onDownload.bind(null, file),
                remove: onRemove.bind(null, file),
            }) : item;

            return listItemNode;
        });

const UploadList: React.FC<UploadListProps> = (props) => {
    const [, forceUpdate] = useState({});
    const [orderedItems, setOrderedItems] = useState(props.items || []);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
    const itemRefs = useRef<{[key: string]: HTMLDivElement | null}>({});

    // 优化：缓存工具函数
    const extname = useCallback((url: string = '') => {
        const temp = url.split('/');
        const filename = temp[temp.length - 1];
        const filenameWithoutSuffix = filename.split(/#|\?/)[0];
        return (/\.[^./\\]*$/.exec(filenameWithoutSuffix) || [''])[0];
    }, []);

    const isImageFileType = useCallback((type: string): boolean => type.indexOf('image/') === 0, []);

    const isImageUrl = useCallback((file: any): boolean => {
        if (file.type) {
            return isImageFileType(file.type);
        }
        const url: string = (file.thumbUrl || file.url) as string;
        const extension = extname(url);
        if (
            /^data:image\//.test(url) ||
            /(webp|svg|png|gif|jpg|jpeg|jfif|bmp|dpg|ico)$/i.test(extension)
        ) {
            return true;
        }
        if (/^data:/.test(url)) {
            return false;
        }
        if (extension) {
            return false;
        }
        return true;
    }, [isImageFileType, extname]);

    // 优化：防抖更新列表顺序
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setOrderedItems(props.items || []);
        }, 10);
        return () => clearTimeout(timeoutId);
    }, [props.items]);

    // 优化：缓存预览文件处理
    useEffect(() => {
        if (props.listType !== 'picture' && props.listType !== 'picture-card') {
            return;
        }

        const items = props.items || [];
        let hasChanges = false;

        items.forEach(file => {
            if (typeof document === 'undefined' ||
                typeof window === 'undefined' ||
                !window.FileReader || !window.File ||
                !(file.originFileObj instanceof File) ||
                file.thumbUrl !== undefined) {
                return;
            }

            file.thumbUrl = '';
            hasChanges = true;

            previewFile(file.originFileObj, (_previewDataUrl: string) => {
                forceUpdate({});
            });
        });

        // 只在有变化时才触发更新
        if (hasChanges) {
            forceUpdate({});
        }
    }, [props.listType, props.items]);

    const handleClose = useCallback((fileToClose: DefaultListArr) => {
        const onRemove = props.onRemove;
        if (onRemove) {
            onRemove(fileToClose);
        }
    }, [props.onRemove]);

    const handleDownLoad = useCallback((file: DefaultListArr) => {
        let {onDownload} = props;
        if (onDownload) {
            onDownload(file);
        }
    }, [props.onDownload]);

    const handlePreview = useCallback((file: DefaultListArr, e: React.MouseEvent<HTMLElement>) => {
        const {onPreview} = props;
        if (!onPreview) {
            return;
        }
        e.preventDefault();
        return onPreview(file);
    }, [props.onPreview]);

    const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', e.currentTarget.innerHTML);
        setDraggedIndex(index);
    }, []);

    const handleDragEnter = useCallback((index: number) => {
        if (draggedIndex !== null && draggedIndex !== index) {
            setDragOverIndex(index);
        }
    }, [draggedIndex]);

    const handleDragEnd = useCallback(() => {
        if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex) {
            const newItems = [...orderedItems];
            const draggedItem = newItems[draggedIndex];
            newItems.splice(draggedIndex, 1);
            newItems.splice(dragOverIndex, 0, draggedItem);

            setOrderedItems(newItems);

            if (props.onDrag) {
                props.onDrag({
                    source: { index: draggedIndex },
                    destination: { index: dragOverIndex }
                });
            }
        }

        setDraggedIndex(null);
        setDragOverIndex(null);
    }, [draggedIndex, dragOverIndex, orderedItems, props]);

    const {
        clsPrefix = 'u-upload',
        listType = 'text',
        showRemoveIcon = false,
        showDownloadIcon = false,
        showPreviewIcon = true,
        progress,
        locale = 'zh-cn',
        fieldid,
        showUploadList,
        iconRender,
        itemRender,
        dragable = false,
        disabled = false,
        removeText,
        downloadText
    } = props;

    // 优化：使用虚拟化列表渲染（对于大量文件）
    const list = useMemo(() => {
        return orderedItems.map((file, index) => (
            <FileItem
                key={file.uid}
                file={file}
                index={index}
                listType={listType}
                clsPrefix={clsPrefix}
                showRemoveIcon={showRemoveIcon}
                showDownloadIcon={showDownloadIcon}
                showPreviewIcon={showPreviewIcon}
                progress={progress}
                locale={locale}
                fieldid={fieldid}
                showUploadList={showUploadList}
                iconRender={iconRender}
                itemRender={itemRender}
                dragable={dragable}
                disabled={disabled}
                removeText={removeText}
                downloadText={downloadText}
                onPreview={handlePreview}
                onRemove={handleClose}
                onDownload={handleDownLoad}
                draggedIndex={draggedIndex}
                dragOverIndex={dragOverIndex}
                onDragStart={handleDragStart}
                onDragEnter={handleDragEnter}
                onDragEnd={handleDragEnd}
                itemRef={(el) => itemRefs.current[file.uid] = el}
                isImageUrl={isImageUrl}
            />
        ));
    }, [orderedItems, listType, clsPrefix, showRemoveIcon, showDownloadIcon, showPreviewIcon,
        progress, locale, fieldid, showUploadList, iconRender, itemRender, dragable, disabled,
        removeText, downloadText, handlePreview, handleClose, handleDownLoad, draggedIndex,
        dragOverIndex, handleDragStart, handleDragEnter, handleDragEnd, isImageUrl]);

    const listClassNames = classNames({
        [`${clsPrefix}-list`]: true,
        [`${clsPrefix}-list-${listType}`]: true,
    });

    return (
        <div className={listClassNames}>
            {
                dragable ? (
                    <div className={`${clsPrefix}-drag-box`}>
                        {list}
                    </div>
                ) : list
            }
        </div>
    );
};

export default WebUI({name: "upload", defaultProps})(UploadList);